import React from "react";
import { motion } from "framer-motion";
import { CREDENTIALS } from "@/constants/testIds";

const ITEMS = [
  {
    year: "2025",
    where: "Panjim, Goa",
    title: "Independent Psychotherapy Practice",
    body:
      "Runs an independent psychodynamic psychotherapy practice in Panjim, Goa — offering long-form therapy to adults, adolescents, and young adults.",
    kind: "Practice",
  },
  {
    year: "2018",
    where: "New Delhi",
    title: "Child & Adolescent Mental Health Team",
    body:
      "Working with a specialist child &amp; adolescent mental health team at The Family Tree, New Delhi, helping shape a psychoanalytically-oriented, referral-led practice.",
    kind: "Practice",
  },
  {
    year: "2019+",
    where: "Ongoing",
    title: "ISTDP — Intensive Short-Term Dynamic Psychotherapy",
    body:
      "Trained in Intensive Short-Term Dynamic Psychotherapy, integrating focused emotional work with a long-form psychodynamic sensibility.",
    kind: "Training",
  },
  {
    year: "2015 – 2017",
    where: "Mumbai",
    title: "Psychoanalytic Training",
    body:
      "Psychoanalytically trained at the Psychoanalytic Training and Research Centre (PTRC), Mumbai.",
    kind: "Training",
  },
  {
    year: "MSc",
    where: "University of Edinburgh, UK",
    title: "Child & Adolescent Mental Health and Psychological Practice",
    body:
      "Graduate research and clinical training in child & adolescent mental health.",
    kind: "Education",
  },
  {
    year: "BA (Hons)",
    where: "Lady Shri Ram College, Delhi University",
    title: "Psychology",
    body:
      "Undergraduate honours degree in Psychology.",
    kind: "Education",
  },
];

export default function Credentials() {
  return (
    <section
      id="credentials"
      data-testid={CREDENTIALS.section}
      className="relative py-28 md:py-40"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end"
        >
          <div className="md:col-span-7">
            <span className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
              (03) &nbsp; Credentials
            </span>
          </div>
          <p className="md:col-span-5 text-sm md:text-base text-muted-foreground max-w-md leading-relaxed">
            Formal training over more than a decade — but the work itself is
            held together by something simpler: attentiveness, curiosity, and
            care.
          </p>
        </motion.div>

        <div className="mt-16 md:mt-20 relative">
          {/* Vertical timeline line */}
          <div
            aria-hidden
            className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-border -translate-x-1/2"
          />

          <div className="space-y-10 md:space-y-14">
            {ITEMS.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{
                  duration: 0.9,
                  delay: i * 0.05,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className={`relative md:grid md:grid-cols-2 md:gap-12 items-start ${
                  i % 2 === 0 ? "" : "md:[&>*:first-child]:order-2"
                }`}
              >
                {/* Dot */}
                <div
                  aria-hidden
                  className="hidden md:block absolute left-1/2 top-6 -translate-x-1/2 w-3 h-3 rounded-full bg-primary ring-4 ring-background"
                />
                <div className={`${i % 2 === 0 ? "md:text-right md:pr-14" : "md:pl-14"}`}>
                  <div className="text-xs uppercase tracking-[0.3em] text-primary">
                    {item.kind}
                  </div>
                  <div className="mt-2 font-serif text-2xl md:text-3xl text-foreground leading-tight">
                    {item.year}
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {item.where}
                  </div>
                </div>
                <div className={`${i % 2 === 0 ? "md:pl-14" : "md:text-right md:pr-14"} mt-4 md:mt-0`}>
                  <div className="bg-card border border-border rounded-3xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow duration-500">
                    <h3 className="font-serif text-xl md:text-2xl text-foreground leading-snug">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-sm md:text-base text-foreground/75 leading-relaxed">
                      {item.body}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
